/**
 * Comentarios del blog — moderación.
 *   - 4 KPI cards: Total / Pendientes / Aprobados / Rechazados
 *   - Tabs: Todos / Pendientes / Aprobados / Rechazados
 *   - Lista con: cliente/guest, contenido, post asociado, fecha, status
 *   - Botones: Aprobar / Rechazar / Eliminar
 */
import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Card, Badge, Button, PageHeader } from '@/components/ui'
import { tenantApi } from '@/lib/api'
import { useAuth } from '@/lib/auth-store'
import { cn } from '@/lib/cn'
import { MessageCircle, Check, X, Trash2 } from 'lucide-react'

export const Route = createFileRoute('/store/$id/comments')({
  component: CommentsPage,
})

interface Comment {
  id: string
  blogPostId: string
  customerId: string | null
  guestName: string | null
  guestEmail: string | null
  content: string
  status: 'pending' | 'approved' | 'rejected'
  createdAt: string
  customerName: string
  customerEmail: string | null
  post: { id: string; slug: string; title: string | null } | null
}

interface CommentsResponse {
  data: Comment[]
  stats: { total: number; pending: number; approved: number; rejected: number }
}

type Tab = 'all' | 'pending' | 'approved' | 'rejected'

function CommentsPage() {
  const { activeTenantId } = useAuth()
  const [tab, setTab] = useState<Tab>('pending')

  const { data, isLoading } = useQuery({
    queryKey: ['blog-comments', tab],
    queryFn: () => tenantApi.get<CommentsResponse>(
      tab === 'all' ? '/api/v1/blog/comments' : `/api/v1/blog/comments?status=${tab}`,
    ),
    enabled: !!activeTenantId,
  })

  const list = data?.data ?? []
  const stats = data?.stats ?? { total: 0, pending: 0, approved: 0, rejected: 0 }

  return (
    <>
      <PageHeader title="Comentarios" description="Moderación de comentarios del blog" />
      <div className="p-8 space-y-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi label="Total" value={stats.total} />
          <Kpi label="Pendientes" value={stats.pending} color="text-yellow-400" />
          <Kpi label="Aprobados" value={stats.approved} color="text-emerald-400" />
          <Kpi label="Rechazados" value={stats.rejected} color="text-red-400" />
        </div>

        <div className="flex items-center gap-1.5 flex-wrap">
          {(['all', 'pending', 'approved', 'rejected'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={cn(
                'px-3 py-1.5 text-sm rounded-md transition border',
                tab === t ? 'bg-neutral-800 text-white border-neutral-700' : 'bg-transparent text-neutral-400 border-transparent hover:text-white',
              )}
            >
              {t === 'all' ? 'Todos' : t === 'pending' ? 'Pendientes' : t === 'approved' ? 'Aprobados' : 'Rechazados'}
            </button>
          ))}
        </div>

        {isLoading && <p className="text-sm text-neutral-500">Cargando...</p>}
        {!isLoading && list.length === 0 && (
          <Card className="p-12 flex flex-col items-center justify-center text-center">
            <MessageCircle className="size-12 text-neutral-700 mb-3" />
            <p className="text-base font-medium">No hay comentarios</p>
            <p className="text-sm text-neutral-500 mt-1">
              {tab === 'pending' ? 'Sin comentarios por moderar.' : 'Aparecerán aquí cuando los lectores comenten en posts del blog.'}
            </p>
          </Card>
        )}

        {list.length > 0 && (
          <div className="space-y-3">
            {list.map((c) => <CommentRow key={c.id} comment={c} />)}
          </div>
        )}
      </div>
    </>
  )
}

function Kpi({ label, value, color }: { label: string; value: number; color?: string }) {
  return (
    <Card className="p-5">
      <p className="text-xs text-neutral-400 uppercase tracking-wider">{label}</p>
      <p className={cn('text-3xl font-bold tabular-nums mt-1', color)}>{value}</p>
    </Card>
  )
}

function CommentRow({ comment }: { comment: Comment }) {
  const qc = useQueryClient()
  const inv = () => qc.invalidateQueries({ queryKey: ['blog-comments'] })
  const approve = useMutation({ mutationFn: () => tenantApi.post(`/api/v1/blog/comments/${comment.id}/approve`, {}), onSuccess: inv })
  const reject = useMutation({ mutationFn: () => tenantApi.post(`/api/v1/blog/comments/${comment.id}/reject`, {}), onSuccess: inv })
  const remove = useMutation({ mutationFn: () => tenantApi.del(`/api/v1/blog/comments/${comment.id}`), onSuccess: inv })

  const statusVariant = comment.status === 'approved' ? 'success' : comment.status === 'pending' ? 'warning' : 'destructive'
  const statusLabel = comment.status === 'approved' ? 'Aprobado' : comment.status === 'pending' ? 'Pendiente' : 'Rechazado'
  const isGuest = !comment.customerId
  const date = new Date(comment.createdAt).toLocaleString('es-CO', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })

  return (
    <Card className="p-5">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-medium">{comment.customerName}</p>
            {isGuest && <Badge variant="muted">Invitado</Badge>}
          </div>
          <p className="text-xs text-neutral-500 mt-0.5">
            {comment.customerEmail && <span>{comment.customerEmail} · </span>}
            {comment.post?.title && (
              <>sobre <span className="text-neutral-300">{comment.post.title}</span> · </>
            )}
            {date}
          </p>
        </div>
        <Badge variant={statusVariant}>{statusLabel}</Badge>
      </div>

      <p className="text-sm text-neutral-300 whitespace-pre-wrap">{comment.content}</p>

      <div className="flex items-center gap-2 mt-4 pt-3 border-t border-neutral-800">
        {comment.status !== 'approved' && (
          <Button size="sm" variant="outline" onClick={() => approve.mutate()} disabled={approve.isPending}>
            <Check className="size-4 mr-2" /> Aprobar
          </Button>
        )}
        {comment.status !== 'rejected' && (
          <Button size="sm" variant="outline" onClick={() => reject.mutate()} disabled={reject.isPending}>
            <X className="size-4 mr-2" /> Rechazar
          </Button>
        )}
        <button
          onClick={() => { if (confirm('¿Eliminar este comentario?')) remove.mutate() }}
          className="ml-auto p-1.5 rounded text-red-400 hover:text-red-300 hover:bg-red-950/40"
          title="Eliminar"
        >
          <Trash2 className="size-4" />
        </button>
      </div>
    </Card>
  )
}
